import React, { useEffect, useState } from 'react';
import type { PeerComparisonResponse, ScoreComparison } from '../../api/rankingApi';
import {
  SCORE_BUCKET_KEYS,
  SCORE_BUCKET_LABELS,
  PEER_COMPARISON_PLACEHOLDER,
  shouldShowPeerComparison,
  clampPercent,
  formatScore,
  formatSkillName,
  formatUpdatedAt,
  hasAnyScoreComparison,
  getPersonalScoresLine,
  getNotPracticedMessage,
  type RingKey,
  type ScoreBucketKey,
} from '../../utils/rankingDisplay';
import './RankingDetailModal.css';
import RankingHighlightedText from './RankingHighlightedText';

export interface RankingDetailModalProps {
  data: PeerComparisonResponse;
  focusRing?: RingKey | null;
  onClose: () => void;
}

const RING_SECTION_IDS: Record<RingKey, string> = {
  consistency: 'ranking-section-consistency',
  technical: 'ranking-section-technical',
  coding: 'ranking-section-coding',
  gd: 'ranking-section-gd',
};

const SCORE_BAR_COLORS: Partial<Record<ScoreBucketKey | 'consistency', string>> = {
  consistency: '#f31e5a',
  overall: '#8B5CF6',
  coding: '#8cf402',
  technical: '#64748b',
  gd: '#00dad9',
};

function scoreSectionId(key: ScoreBucketKey): string | undefined {
  if (key === 'technical') return 'ranking-section-technical';
  if (key === 'coding') return 'ranking-section-coding';
  if (key === 'gd') return 'ranking-section-gd';
  return undefined;
}

function isScoreRingFocused(key: ScoreBucketKey, focusRing?: RingKey | null): boolean {
  if (!focusRing) return false;
  return (
    (key === 'technical' && focusRing === 'technical') ||
    (key === 'coding' && focusRing === 'coding') ||
    (key === 'gd' && focusRing === 'gd')
  );
}

const RankingDetailModal: React.FC<RankingDetailModalProps> = ({ data, focusRing, onClose }) => {
  const consistency = data.consistency;
  const engagement = data.engagement;
  const comparisons = data.score_comparisons;
  const showScoreCards = hasAnyScoreComparison(data);
  const personalScores = getPersonalScoresLine(data);
  const updatedLabel = formatUpdatedAt(data.rankings_computed_at);

  useEffect(() => {
    if (!focusRing) return;
    const id = RING_SECTION_IDS[focusRing];
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [focusRing]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const consistencyPct = clampPercent(consistency?.better_than_percent);
  const practiceDays = consistency?.practice_days_in_window;
  const currentStreak = consistency?.current_streak_days;
  const longestStreak = consistency?.longest_streak_days;

  const consistencyStats: string[] = [];
  if (practiceDays != null) consistencyStats.push(`${practiceDays}/30 practice days`);
  if (currentStreak != null) consistencyStats.push(`${currentStreak}-day streak`);
  if (longestStreak != null) consistencyStats.push(`${longestStreak}-day best`);

  const emptyBuckets = SCORE_BUCKET_KEYS.filter(
    (key) => comparisons?.[key]?.available === false
  );

  const hasInsightsContent = Boolean(engagement) || !showScoreCards;

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const renderScoreRow = (key: ScoreBucketKey, bucket: ScoreComparison) => {
    const pct = clampPercent(bucket.better_than_percent);
    const sectionId = scoreSectionId(key);
    const hasMeta =
      bucket.your_score != null || pct != null || bucket.session_count != null;

    return (
      <div
        key={key}
        id={sectionId}
        className={`ranking-detail-score-row ${isScoreRingFocused(key, focusRing) ? 'is-focused' : ''}`}
      >
        <span className="ranking-detail-score-label">{SCORE_BUCKET_LABELS[key]}</span>

        {pct != null && (
          <div className="ranking-detail-bar-wrap">
            <div
              className="ranking-detail-bar"
              style={{
                width: `${pct >= 99 ? 100 : pct}%`,
                backgroundColor: SCORE_BAR_COLORS[key] ?? '#8B5CF6',
              }}
            />
          </div>
        )}

        {bucket.message && (
          <p className="ranking-detail-score-message">
            <RankingHighlightedText text={bucket.message} />
          </p>
        )}

        {hasMeta && (
          <div className="ranking-detail-score-meta">
            {bucket.your_score != null && <span>Score {formatScore(bucket.your_score)}</span>}
            {pct != null && (
              <>
                {bucket.your_score != null && (
                  <span className="ranking-detail-score-meta-sep" aria-hidden="true">
                    ·
                  </span>
                )}
                <span>
                  <span className="ranking-pct">{Math.round(pct)}%</span> vs peers
                </span>
              </>
            )}
            {bucket.session_count != null && (
              <>
                {(bucket.your_score != null || pct != null) && (
                  <span className="ranking-detail-score-meta-sep" aria-hidden="true">
                    ·
                  </span>
                )}
                <span>{bucket.session_count} sessions</span>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const hasConsistencyBar =
    consistency?.message || consistencyStats.length > 0 || consistencyPct != null;

  return (
    <div className={`ranking-detail-panel${isVisible ? ' ranking-detail-panel--open' : ''}`}>
      <div className="ranking-detail-backdrop" role="presentation" onClick={onClose} />

      <aside
        className="ranking-detail-panel__body"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ranking-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ranking-detail-panel__header">
          <div className="ranking-detail-panel__header-info">
            <h2 id="ranking-detail-title" className="ranking-detail-panel__title">
              Peer rankings
            </h2>
            {data.cohort_size != null && (
              <p className="ranking-detail-panel__subtitle">
                Among {data.cohort_size} peers
                {updatedLabel ? ` · Updated ${updatedLabel}` : ''}
              </p>
            )}
          </div>
          <button
            type="button"
            className="ranking-detail-panel__close"
            aria-label="Close"
            onClick={onClose}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="ranking-detail-panel__content">
          <div className="ranking-detail-bars-list">
            {hasConsistencyBar && (
              <div
                id="ranking-section-consistency"
                className={`ranking-detail-score-row ${focusRing === 'consistency' ? 'is-focused' : ''}`}
              >
                <span className="ranking-detail-score-label">Consistency</span>

                {consistencyPct != null && (
                  <div className="ranking-detail-bar-wrap">
                    <div
                      className="ranking-detail-bar"
                      style={{
                        width: `${consistencyPct >= 99 ? 100 : consistencyPct}%`,
                        backgroundColor: SCORE_BAR_COLORS.consistency,
                      }}
                    />
                  </div>
                )}

                {consistency?.message && (
                  <p className="ranking-detail-score-message">
                    <RankingHighlightedText text={consistency.message} />
                  </p>
                )}

                {consistencyStats.length > 0 && (
                  <div className="ranking-detail-score-meta">{consistencyStats.join(' · ')}</div>
                )}
              </div>
            )}

            {showScoreCards &&
              SCORE_BUCKET_KEYS.map((key) => {
                const bucket = comparisons?.[key];
                if (!bucket || !shouldShowPeerComparison(bucket)) return null;
                return renderScoreRow(key, bucket);
              })}
          </div>

          {hasInsightsContent && (
            <div className="ranking-detail-insights-panel">
              {engagement && (
                <section className="ranking-detail-section">
                  <h3 className="ranking-detail-section-title">Engagement</h3>
                  {engagement.strongest_skill?.name && (
                    <p className="ranking-detail-line">
                      <span className="ranking-detail-line-label">Strongest:</span>{' '}
                      {formatSkillName(engagement.strongest_skill.name)}
                      {engagement.strongest_skill.better_than_percent != null && (
                        <span className="ranking-detail-line-meta">
                          {' '}
                          ·{' '}
                          <span className="ranking-pct">
                            {Math.round(engagement.strongest_skill.better_than_percent)}%
                          </span>{' '}
                          vs peers
                        </span>
                      )}
                    </p>
                  )}
                  {engagement.growth_area?.name && (
                    <p className="ranking-detail-line">
                      <span className="ranking-detail-line-label">Focus:</span>{' '}
                      {formatSkillName(engagement.growth_area.name)}
                      {engagement.growth_area.gap_vs_org_avg != null && (
                        <span className="ranking-detail-line-meta">
                          {' '}
                          · {engagement.growth_area.gap_vs_org_avg > 0 ? '+' : ''}
                          {formatScore(engagement.growth_area.gap_vs_org_avg)} vs org avg
                        </span>
                      )}
                    </p>
                  )}
                  {engagement.practice_volume_percentile != null && (
                    <div className="ranking-detail-metric">
                      <p className="ranking-detail-line">
                        Practice volume:{' '}
                        <span className="ranking-pct">
                          {Math.round(engagement.practice_volume_percentile)}%
                        </span>{' '}
                        vs peers
                      </p>
                      <p className="ranking-detail-line-subtext">
                        How your total practice sessions compare to peers in your org.
                      </p>
                    </div>
                  )}
                  {engagement.skills_breadth_percentile != null && (
                    <div className="ranking-detail-metric">
                      <p className="ranking-detail-line">
                        Skills breadth:{' '}
                        <span className="ranking-pct">
                          {Math.round(engagement.skills_breadth_percentile)}%
                        </span>{' '}
                        vs peers
                      </p>
                      <p className="ranking-detail-line-subtext">
                        How many different skills you&apos;ve practiced compared to peers.
                      </p>
                    </div>
                  )}
                </section>
              )}

              {!showScoreCards && (
                <section className="ranking-detail-section">
                  <div className="ranking-detail-placeholder">
                    <p className="ranking-detail-placeholder-title">
                      {PEER_COMPARISON_PLACEHOLDER.title}
                    </p>
                    <p className="ranking-detail-placeholder-body">
                      {PEER_COMPARISON_PLACEHOLDER.body}
                    </p>
                    {personalScores && (
                      <p className="ranking-detail-personal-scores">
                        Your averages — {personalScores}
                      </p>
                    )}
                  </div>
                </section>
              )}

              {!showScoreCards && emptyBuckets.length > 0 && (
                <section className="ranking-detail-section ranking-detail-empty-list">
                  {emptyBuckets.map((key) => (
                    <p key={key} className="ranking-detail-empty-bucket">
                      {getNotPracticedMessage(key)}
                    </p>
                  ))}
                </section>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

export default RankingDetailModal;
